import React, { useEffect, useMemo, useState } from "react";
import { PropsWithChildren } from "react";
import { ReactNode } from "react";

export const Tab = ({
  children,
  Icon,
  hidden
}: PropsWithChildren<{
  tabLabel: ReactNode;
  Icon?: (props: { className?: string, isSelected: boolean }) => JSX.Element;
  hidden?: boolean;
}>) => <section hidden={hidden}>{children}</section>;

/**
 * A special type of container that splits child <Tab />'s
 * into separate tabs - with a navbar header to navigate
 * between tabs.
 *
 * Does not render tab navbar if less than two tabs exist.
 *
 * Usage:
 *
 * ```
 * <TabbedLayout>
 *  <h1>Other children work too - like this header!</h1>
 *
 *  <Tab tabLabel="Tab 1">
 *    {content}
 *  </Tab>
 *
 *  <Tab tabLabel="Tab 2">
 *    {content}
 *  </Tab>
 * </TabbedLayout>
 * ```
 */
const TabbedLayout = ({ children }: PropsWithChildren) => {
  const [activeTab, setActiveTab] = useState("");

  const tabs = useMemo(
    () =>
      React.Children.map(children, child => {
        if (React.isValidElement(child) && "props" in child) {
          return child.props.tabLabel
            ? {
                label: child.props.tabLabel as string,
                Icon: child.props.Icon as
                  | ((props: { className?: string, isSelected: boolean }) => JSX.Element)
                  | undefined
              }
            : undefined;
        }
      })?.filter(t => t !== undefined) || [],
    [children]
  );

  const content = useMemo(
    () =>
      React.Children.map(children, child => {
        if (React.isValidElement(child) && "props" in child) {
          return child.props.tabLabel
            ? React.createElement(child.type, {
                ...child.props,
                hidden: child.props.tabLabel !== activeTab
              })
            : child;
        }

        return child;
      }),
    [activeTab, children]
  );

  useEffect(() => {
    if (activeTab === "" && tabs.length > 0) setActiveTab(tabs[0].label);
  }, [tabs, activeTab]);

  return (
    <section>
      {tabs.length < 2 ? (
        <>{children}</>
      ) : (
        <>
          <nav className="mb-5 flex border-b-2 border-black ">
            {tabs.map(({ label, Icon }, index) => (
              <p
                key={index}
                className={
                  "flex w-fit align-left gap-2 rounded-t-md py-4 px-24 transition text-lg" +
                  (activeTab === label
                    ? " bg-ctb-blue-600 text-white hover:cursor-default"
                    : " hover:cursor-pointer")
                }
                onClick={() => {
                  if (activeTab !== label) setActiveTab(label);
                }}
              >
                {Icon && (
                 <Icon
                 className={`translate-y-1 text-2xl ${
                   activeTab === label ? " stroke-white" : "stroke-ctb-grey-600"
                 }`}
                 isSelected={activeTab === label} 
               />
               
                )}
                {label}
              </p>
            ))}
          </nav>

          {/* Render the children - but only render tab content if the tab is active */}
          {content}
        </>
      )}
    </section>
  );
};

export default TabbedLayout;
